import type { components, operations } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

/**
 * Admin feedback inbox + report detail (design spec §5.1, §5.2).
 *
 * Platform administrators triage reports here; the company SPA only submits
 * them. The endpoints are platform-guarded and cross-tenant, so the APIs below
 * call `/platform/feedback-reports` exactly as the design specifies. They are
 * generated into the api-client contract in the same commit as the backend
 * routes (AGENTS.md §7); until then these paths are not yet in
 * `schema.d.ts`, so this file typechecks once the contract is regenerated.
 */

export const FEEDBACK_STATUSES = ['new', 'reviewing', 'resolved', 'closed'] as const
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number]

export const FEEDBACK_TYPES = ['problem', 'suggestion'] as const
export type FeedbackType = (typeof FEEDBACK_TYPES)[number]

export const FEEDBACK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number]

export const feedbackStatusSchema = z.enum(FEEDBACK_STATUSES)
export const feedbackTypeSchema = z.enum(FEEDBACK_TYPES)

/** One row of the inbox table — the summary the list endpoint returns. */
export type FeedbackRow = components['schemas']['FeedbackReportResource'] & {
  type: FeedbackType
  status: FeedbackStatus
  priority: FeedbackPriority
}
export type FeedbackReportSummary = FeedbackRow

/** A single report with the triage context only an admin may see (design §5.2). */
export type FeedbackDetail = components['schemas']['FeedbackReportDetailResource'] & {
  type: FeedbackType
  status: FeedbackStatus
  priority: FeedbackPriority
}
export type FeedbackReportDetail = FeedbackDetail
export type FeedbackReport = FeedbackDetail

/** A Laravel-style paginated envelope: `data` is the page, `meta` the cursor. */
export type FeedbackReportsPage = Omit<
  operations['platform.feedback-reports.index']['responses'][200]['content']['application/json'],
  'data'
> & { data: FeedbackRow[] }

/**
 * The inbox's URL search params.
 *
 * Status, type and company filters, plus the page, live in the URL so a
 * filtered or paginated view is linkable and survives a reload (AGENTS.md).
 * Zod validates at the route boundary; the inferred type flows into the screen.
 */
export const feedbackSearchSchema = z.object({
  status: feedbackStatusSchema.optional(),
  type: feedbackTypeSchema.optional(),
  company_id: z.string().optional(),
  company: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
})

export type FeedbackSearch = z.infer<typeof feedbackSearchSchema>

/** Canonical name for the inbox's URL search params (plan 63, task 7). */
export type FeedbackFilters = FeedbackSearch

export const feedbackKeys = {
  all: ['platform', 'feedback-reports'] as const,
  list: (search: FeedbackSearch) => [...feedbackKeys.all, 'list', search] as const,
  detail: (id: string) => [...feedbackKeys.all, id] as const,
}

/**
 * The paginated, filtered inbox (design §5.1).
 *
 * The queryKey includes the search so a filter change refetches; the page
 * ordering is newest-first, decided by the API.
 */
export function useFeedbackReports(search: FeedbackSearch) {
  return useQuery({
    queryKey: feedbackKeys.list(search),
    queryFn: async (): Promise<FeedbackReportsPage> => {
      const { status, type, company_id, company, page } = search
      const params: Record<string, string | number> = {}
      if (status) params.status = status
      if (type) params.type = type
      if (company_id) params.company_id = company_id
      else if (company && company.trim() !== '') params.company_id = company.trim()
      if (page && page > 1) params.page = page

      const result = await api.GET('/platform/feedback-reports', {
        params: { query: params },
      })
      if (!result.data) {
        throw new Error('GET /platform/feedback-reports returned no body')
      }
      return result.data as FeedbackReportsPage
    },
  })
}

export const useFeedbackList = useFeedbackReports

/** One report's detail with reporter/company context (design §5.2). */
export function useFeedbackReport(reportId: string | null) {
  return useQuery({
    queryKey: feedbackKeys.detail(reportId ?? ''),
    enabled: Boolean(reportId),
    queryFn: async (): Promise<FeedbackReportDetail> => {
      const result = await api.GET('/platform/feedback-reports/{report}', {
        params: { path: { report: reportId ?? '' } },
      })
      if (!result.data) {
        throw new Error('GET /platform/feedback-reports/{report} returned no body')
      }
      return result.data.data as FeedbackReportDetail
    },
  })
}

export interface TriageFeedbackInput {
  status?: FeedbackStatus
  priority?: FeedbackPriority
  internal_notes?: string | null
}

/**
 * The only write an admin may make: status, priority and internal notes
 * (design §5.2, §12). `TriageFeedbackReport` audits the change; this hook just
 * invalidates the list and the single report so both views stay truthful.
 */
export function useTriageFeedbackReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      reportId,
      id,
      input,
    }: {
      reportId?: string
      id?: string
      input: TriageFeedbackInput
    }) =>
      api.PATCH('/platform/feedback-reports/{report}', {
        params: { path: { report: reportId ?? id ?? '' } },
        body: input,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'feedback-reports'] })
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'feedback-reports', variables.reportId ?? variables.id],
      })
    },
  })
}

export const useTriageFeedback = useTriageFeedbackReport
